import { inject, Injectable } from '@angular/core';
import { Observable, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@shs-digi-care-monorepo/frontend-lib';
import {
    EFileEntity,
    EMPTY_PATH_PREFIX,
    IDeleteRequestFileInput,
    IDownloadRequestAttachmentInput,
    IListFileInput,
    IS3File
} from '@shs-digi-care-monorepo/interfaces';

export interface UploadFileInput {
    fileEntity: EFileEntity;
    requestId: string;
    commentId?: string;
    file: File;
    fileName: string;
}

/**
 * Service for managing IRO request attachments via API.
 */
@Injectable({
    providedIn: 'root'
})
export class AttachmentService {
    protected requestEndpoint = 'iro-nam-requests';
    private readonly httpClient = inject(HttpClient);

    /**
     * Uploads a file by first obtaining a signed URL and then uploading to S3.
     * @param input The upload file input
     * @returns Observable of the upload result
     */
    public uploadFile(input: UploadFileInput): Observable<unknown> {
        const url = this.getUploadServiceUrl(input);
        const contentType = input.file.type || 'application/octet-stream';

        return this.httpClient
            .get<{ signedURL: string }>(url, {
                params: { contentType }
            })
            .pipe(switchMap((uploadUrl) => this.uploadFileToS3(uploadUrl.signedURL, input.file)));
    }

    /**
     * Uploads a file directly to S3 using a pre-signed URL.
     * @param signedUrl The pre-signed S3 URL
     * @param file The file to upload
     * @returns Observable of the upload result
     */
    public uploadFileToS3(signedUrl: string, file: File): Observable<unknown> {
        const contentType = file.type || 'application/octet-stream';
        return this.httpClient.put(signedUrl, file, {
            headers: { 'Content-Type': contentType, 'If-None-Match': '*' }
        });
    }

    /**
     * Downloads a file by obtaining a signed download URL and triggering browser download.
     * @param input The download attachment input
     * @returns Observable of the signed URL response
     */
    public downloadFile(input: IDownloadRequestAttachmentInput): Observable<{ signedURL: string }> {
        const url = this.getDownloadServiceUrl(input);
        return this.httpClient.get<{ signedURL: string }>(url).pipe(
            tap((downloadUrl) => {
                const link = document.createElement('a');
                link.setAttribute('target', '_blank');
                link.setAttribute('href', downloadUrl.signedURL);
                link.setAttribute('download', input.filePath);
                link.click();
                link.remove();
            })
        );
    }

    /**
     * Deletes a file attachment from a request.
     * @param input The delete file input
     * @returns Observable of the delete result
     */
    public deleteFile(input: IDeleteRequestFileInput): Observable<unknown> {
        const url = this.getDeleteServiceUrl(input);
        return this.httpClient.delete(url);
    }

    /**
     * Lists files attached to a request or comment.
     * @param input The list file input
     * @returns Observable of S3 file metadata
     */
    public listFiles(input: IListFileInput): Observable<IS3File[]> {
        const url = this.getListServiceUrl(input);
        return this.httpClient.get<IS3File[]>(url);
    }

    /**
     * Builds the upload URL based on the file entity type.
     */
    private getUploadServiceUrl(input: UploadFileInput): string {
        const encodedFileName = encodeURIComponent(input.fileName);
        switch (input.fileEntity) {
            case EFileEntity.Request:
                return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/attachments/${encodedFileName}/links/upload`;
            case EFileEntity.Comment:
                if (!input.commentId) {
                    throw new Error('Comment ID is required for comment file uploads');
                }
                return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/comments/${input.commentId}/attachments/${encodedFileName}/links/upload`;
            default:
                throw new Error('Invalid file entity');
        }
    }

    /**
     * Builds the download URL based on the file entity type.
     */
    private getDownloadServiceUrl(input: IDownloadRequestAttachmentInput): string {
        const encodedFilePath = encodeURIComponent(input.filePath);
        switch (input.fileEntity) {
            case EFileEntity.Request:
                return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/attachments/${encodedFilePath}/links/download`;
            case EFileEntity.Comment:
                if (!input.commentId) {
                    throw new Error('Comment ID is required for comment file downloads');
                }
                // Files are stored under comments/{fileName} within the request's S3 prefix
                return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/attachments/${encodeURIComponent(`comments/${input.filePath}`)}/links/download`;
            default:
                throw new Error('Invalid file entity');
        }
    }

    /**
     * Builds the delete URL for a request attachment.
     */
    private getDeleteServiceUrl(input: IDeleteRequestFileInput): string {
        const encodedFilePath = encodeURIComponent(input.filePath);
        return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/attachments/${encodedFilePath}`;
    }

    /**
     * Builds the list URL based on the file entity type.
     */
    private getListServiceUrl(input: IListFileInput): string {
        const prefix = input.prefix || EMPTY_PATH_PREFIX;
        const encodedPrefix = encodeURIComponent(prefix);
        switch (input.fileEntity) {
            case EFileEntity.Request:
                return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/attachments/${encodedPrefix}`;
            case EFileEntity.Comment:
                if (!input.commentId) {
                    throw new Error('Comment ID is required for listing comment files');
                }
                return `${environment.apiUrl}/${this.requestEndpoint}/${input.requestId}/comments/${input.commentId}/attachments/${encodedPrefix}`;
            default:
                throw new Error('Invalid file entity');
        }
    }
}
